import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { PKGNeighbourhoodPanel } from '../components/pkg/PKGNeighbourhoodPanel';
import DispositionCard, { DispositionOption } from '../components/DispositionCard';
import OwnerReassignControl, { useProjectOwnership } from '../components/OwnerReassignControl';
import { useOwnerDirectory } from '../hooks/useOwnerDirectory';
import { CANONICAL_PHASES, CANONICAL_PHASE_LABELS, canonicalPhaseLabel, isCanonicalPhase } from '@blueprint/shared';
// AOQ-5 (#2921, §2.6) — the phase FILTER already existed here (#2611); this
// story adds the visual BADGE half so an Architecture-phase risk reads as
// distinct from a PRD-phase one at a glance, not just via the filter
// dropdown. Same component OpenQuestionsPage.tsx now also uses, so both
// registers' combined views share one phase-badge look.
import PhaseBadge, { type PhaseKey } from '../components/PhaseBadge';

// #2611 — one source of both phase dropdowns on this page (filterPhase +
// the carry-forward Target-phase field below); eliminates the two divergent
// copies (a stale 5-of-10 hardcoded filter list, and a free-text field with
// no vocabulary at all) that made carried-forward risks unqueryable by phase.
const PHASE_OPTIONS = CANONICAL_PHASES.map((key) => ({ value: key, label: CANONICAL_PHASE_LABELS[key] }));

// ── Types ─────────────────────────────────────────────────────────────────────

// #2544 (Model A generalized) — the agent's unreviewed disposition proposal
// for a still-OPEN risk, merged into the list response by the GET route
// (never a separate fetch — see routes/risks.ts). null for a risk with no
// pending proposal.
interface RiskDispositionProposal {
  id: string;
  disposition: 'MITIGATE' | 'ACCEPT' | 'CARRIED_FORWARD';
  proposedMitigationPlan: string | null;
  proposedRationale: string | null;
  proposedCarryForwardPhase: string | null;
  proposedCarryForwardOwner: string | null;
  basis: string | null;
}

interface Risk {
  id: string;
  externalId: string | null;
  title: string;
  description: string | null;
  impact: string;
  likelihood: string;
  status: string;
  phaseId: string | null;
  mitigationPlan: string | null;
  outcome: string | null;
  carriedForwardToPhase: string | null;
  carriedForwardOwner: string | null;
  // #2846 — the accountable project member (#2741's ownership model),
  // distinct from carriedForwardOwner's free-text carry-forward note.
  ownerId: string | null;
  createdAt: string;
  /**
   * PKGV.A2 (#793) — the pkg_nodes.id anchoring this risk, resolved
   * server-side via a join in getRisksByProject (#1783, merged
   * e12d6d03). `null` when no PKG node exists yet for this risk.
   */
  pkgNodeId: string | null;
  proposal: RiskDispositionProposal | null;
}

interface RiskSummary {
  total: number;
  open: number;
  high: number;
  critical: number;
}

interface RiskFormData {
  title: string;
  description: string;
  impact: string;
  likelihood: string;
  status: string;
  mitigationPlan: string;
}

// ── Style maps ────────────────────────────────────────────────────────────────

const IMPACT_STYLE: Record<string, { bg: string; color: string }> = {
  CRITICAL: { bg: 'rgba(239,68,68,0.12)',    color: '#ef4444' },
  HIGH:     { bg: 'rgba(245,158,11,0.12)',   color: '#f59e0b' },
  MEDIUM:   { bg: 'rgba(99,102,241,0.12)',   color: 'var(--primary)' },
  LOW:      { bg: 'rgba(120,113,108,0.12)',  color: 'var(--text-secondary)' },
};

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  OPEN:            { bg: 'rgba(245,158,11,0.12)',  color: '#f59e0b' },
  MITIGATED:       { bg: 'rgba(99,102,241,0.12)',  color: 'var(--primary)' },
  ACCEPTED:        { bg: 'rgba(120,113,108,0.12)', color: 'var(--text-secondary)' },
  CLOSED:          { bg: 'rgba(16,185,129,0.12)',  color: '#10b981' },
  // #2544
  CARRIED_FORWARD: { bg: 'rgba(120,113,108,0.12)', color: 'var(--text-secondary)' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function Badge({ text, style }: { text: string; style: { bg: string; color: string } }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 9999,
      fontSize: 11, fontWeight: 600, fontFamily: 'var(--font-body)',
      background: style.bg, color: style.color, letterSpacing: '0.02em',
    }}>
      {text}
    </span>
  );
}

function SummaryCard({ value, label, color }: { value: number; label: string; color: string }) {
  return (
    <div style={{
      flex: 1, minWidth: 130, padding: '16px 20px', borderRadius: 'var(--radius)',
      border: '1px solid var(--border)', background: 'var(--surface)',
    }}>
      <div style={{ fontSize: 28, fontWeight: 700, color, fontFamily: 'var(--font-display)', lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'var(--font-body)', marginTop: 4 }}>
        {label}
      </div>
    </div>
  );
}

const EMPTY_FORM: RiskFormData = {
  title: '', description: '', impact: 'MEDIUM', likelihood: 'MEDIUM', status: 'OPEN', mitigationPlan: '',
};

// ── Main page ─────────────────────────────────────────────────────────────────

export default function RisksPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const ownership = useProjectOwnership(projectId);
  // #3165 — named-party suggestions for the carry-forward "Owner" field
  // (distinct from `ownership` above, which backs the ownerId reassign
  // control, a closed set of platform accounts).
  const ownerDirectory = useOwnerDirectory(projectId);

  const [risks, setRisks]       = useState<Risk[]>([]);
  const [summary, setSummary]   = useState<RiskSummary | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [bridging, setBridging] = useState(false);
  const [bridgeMsg, setBridgeMsg] = useState<string | null>(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterImpact, setFilterImpact] = useState('');
  const [filterPhase, setFilterPhase]   = useState('');
  // #2620 — additive: filters by the phase a risk was carried FORWARD to,
  // distinct from filterPhase (the phase it was RAISED in). Same canonical
  // PHASE_OPTIONS source; does not touch filterPhase or its predicate.
  const [filterCarriedForwardToPhase, setFilterCarriedForwardToPhase] = useState('');

  // Inline status edit
  const [editingStatusId, setEditingStatusId] = useState<string | null>(null);

  // Add/Edit form
  const [showForm, setShowForm] = useState(false);
  const [editingRisk, setEditingRisk] = useState<Risk | null>(null);
  const [form, setForm] = useState<RiskFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Open form if ?add=1 param
  useEffect(() => {
    if (searchParams.get('add') === '1') {
      setShowForm(true);
      setEditingRisk(null);
      setForm(EMPTY_FORM);
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    Promise.all([
      apiFetch<Risk[]>(`/projects/${projectId}/risks`),
      apiFetch<RiskSummary>(`/projects/${projectId}/risks/summary`),
    ])
      .then(([r, s]) => { setRisks(r); setSummary(s); })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    if (!projectId) return;
    setBridging(true);
    setBridgeMsg(null);
    try {
      const result = await apiFetch<{ risksCreated: number; risksUpdated: number; errors: string[] }>(
        `/projects/${projectId}/bridge-risks`,
        { method: 'POST' },
      );
      setBridgeMsg(`Synced: ${result.risksCreated} created, ${result.risksUpdated} updated`);
      load();
    } catch (e) {
      setBridgeMsg(`Error: ${String(e)}`);
    } finally {
      setBridging(false);
    }
  };

  // #2544 (Model A generalized) — same "on demand, not automatic" convention
  // as Open Questions' own "Generate agent proposals" button: an LLM call
  // per run, never auto-triggered on page load.
  const [generatingProposals, setGeneratingProposals] = useState(false);
  const [proposalError, setProposalError] = useState<string | null>(null);
  const [dispositioningId, setDispositioningId] = useState<string | null>(null);
  const [dispositionError, setDispositionError] = useState<string | null>(null);

  const handleGenerateProposals = async () => {
    if (!projectId) return;
    setGeneratingProposals(true);
    setProposalError(null);
    try {
      await apiFetch(`/projects/${projectId}/risks/generate-disposition-proposals`, { method: 'POST' });
      load();
    } catch (e) {
      setProposalError(String(e));
    } finally {
      setGeneratingProposals(false);
    }
  };

  // Parameterizes the shared DispositionCard (Part 1 of the shared core)
  // with Risk's own option-set — Mitigate/Accept/Carried-forward, per this
  // story's option-set. The generic card knows nothing about "risk"; it
  // only sees labelled options and their required text fields.
  const DISPOSITION_OPTIONS: DispositionOption[] = [
    { key: 'MITIGATE', label: 'Mitigate', fields: [{ key: 'mitigationPlan', label: 'Mitigation plan' }] },
    { key: 'ACCEPT', label: 'Accept', fields: [{ key: 'rationale', label: 'Rationale' }] },
    {
      key: 'CARRIED_FORWARD', label: 'Carry forward',
      fields: [
        { key: 'carryForwardPhase', label: 'Target phase', options: PHASE_OPTIONS },
        {
          key: 'carryForwardOwner', label: 'Owner',
          placeholder: 'e.g. client sponsor, platform team',
          short: true,
          // #3165 — suggestions from the project directory; "Other…" keeps
          // the free-text path for a team/role/external party not in it.
          directoryOptions: ownerDirectory.entries.map((e) => ({ value: e.key, label: e.label })),
        },
      ],
    },
  ];

  async function handleAcceptDisposition(risk: Risk, optionKey: string, fieldValues: Record<string, string>) {
    if (!projectId) return;
    setDispositioningId(risk.id);
    setDispositionError(null);
    try {
      await apiFetch(`/projects/${projectId}/risks/${risk.id}/accept-disposition`, {
        method: 'POST',
        body: JSON.stringify({
          proposalId: risk.proposal?.id,
          disposition: optionKey,
          mitigationPlan: fieldValues.mitigationPlan,
          rationale: fieldValues.rationale,
          carryForwardPhase: fieldValues.carryForwardPhase,
          carryForwardOwner: fieldValues.carryForwardOwner,
        }),
      });
      load();
    } catch (e) {
      setDispositionError(String(e));
    } finally {
      setDispositioningId(null);
    }
  }

  const handleStatusChange = async (risk: Risk, newStatus: string) => {
    setEditingStatusId(null);
    const prev = risks;
    setRisks(rs => rs.map(r => r.id === risk.id ? { ...r, status: newStatus } : r));
    try {
      await apiFetch(`/risks/${risk.id}`, { method: 'PATCH', body: JSON.stringify({ status: newStatus }) });
      apiFetch<RiskSummary>(`/projects/${projectId}/risks/summary`).then(setSummary).catch(() => {});
    } catch {
      setRisks(prev);
    }
  };

  const openAddForm = () => {
    setEditingRisk(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowForm(true);
  };

  const openEditForm = (risk: Risk) => {
    setEditingRisk(risk);
    setForm({
      title: risk.title,
      description: risk.description ?? '',
      impact: risk.impact,
      likelihood: risk.likelihood,
      status: risk.status,
      mitigationPlan: risk.mitigationPlan ?? '',
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleFormSubmit = async () => {
    if (!form.title.trim()) { setFormError('Title is required'); return; }
    setSaving(true);
    setFormError(null);
    try {
      if (editingRisk) {
        await apiFetch(`/risks/${editingRisk.id}`, {
          method: 'PATCH',
          body: JSON.stringify({
            title: form.title.trim(),
            description: form.description.trim() || undefined,
            impact: form.impact,
            likelihood: form.likelihood,
            status: form.status,
            mitigationPlan: form.mitigationPlan.trim() || undefined,
          }),
        });
      } else {
        await apiFetch(`/projects/${projectId}/risks`, {
          method: 'POST',
          body: JSON.stringify({
            title: form.title.trim(),
            description: form.description.trim() || undefined,
            impact: form.impact,
            likelihood: form.likelihood,
            status: form.status,
            mitigationPlan: form.mitigationPlan.trim() || undefined,
          }),
        });
      }
      setShowForm(false);
      load();
    } catch (e) {
      setFormError(String(e));
    } finally {
      setSaving(false);
    }
  };

  // Filtered list
  const filtered = risks.filter(r => {
    if (filterStatus && r.status !== filterStatus) return false;
    if (filterImpact && r.impact !== filterImpact) return false;
    if (filterPhase && r.phaseId !== filterPhase) return false;
    if (filterCarriedForwardToPhase && r.carriedForwardToPhase !== filterCarriedForwardToPhase) return false;
    return true;
  });

  // Optional polish (#2620): only show the carried-forward-to filter when at
  // least one risk actually has a carriedForwardToPhase — an empty-state
  // control here would just be noise. Kept visible (below) once a value is
  // selected even if a later reload zeroes this out, so an active filter
  // never silently zeroes the list with no visible control to clear it
  // (R1 finding, #2620).
  const hasCarriedForwardRisks = risks.some(r => r.carriedForwardToPhase);

  // ── Styles ──────────────────────────────────────────────────────────────────

  const selectStyle: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 12, padding: '6px 10px',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer',
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 13, padding: '8px 12px',
    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    background: 'var(--surface)', color: 'var(--text)', width: '100%',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
    color: 'var(--text-secondary)', display: 'block', marginBottom: 4,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  };

  const btnPrimary: React.CSSProperties = {
    fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
    padding: '8px 20px', borderRadius: 'var(--radius-sm)',
    background: 'var(--primary)', color: '#fff', border: 'none',
    cursor: 'pointer',
  };

  const btnGhost: React.CSSProperties = {
    ...btnPrimary, background: 'transparent', color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
  };

  return (
    <main style={{ flex: 1, overflow: 'auto', padding: '32px 40px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <Link
              to={`/projects/${projectId}`}
              style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}
            >
              ← Dashboard
            </Link>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginTop: 6 }}>
              Risk Register
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {bridgeMsg && (
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: bridgeMsg.startsWith('Synced') ? '#10b981' : '#ef4444' }}>
                {bridgeMsg}
              </span>
            )}
            <button
              onClick={() => void handleSync()}
              disabled={bridging}
              style={{ ...btnGhost, opacity: bridging ? 0.7 : 1, cursor: bridging ? 'not-allowed' : 'pointer' }}
            >
              {bridging ? 'Syncing…' : 'Sync from Backlog'}
            </button>
            <button onClick={openAddForm} style={btnPrimary}>
              + Add Risk
            </button>
          </div>
        </div>

        {/* Summary cards */}
        {summary && (
          <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
            <SummaryCard value={summary.total}    label="Total Risks"   color="var(--text)" />
            <SummaryCard value={summary.open}     label="Open"          color="#f59e0b" />
            <SummaryCard value={summary.high}     label="High Impact"   color="#f59e0b" />
            <SummaryCard value={summary.critical} label="Critical"      color="#ef4444" />
          </div>
        )}

        {/* #2544 (Model A generalized) — on demand, not automatic: an LLM
            call per run, same convention as Open Questions' own button. */}
        {risks.some(r => r.status === 'OPEN') && (
          <div style={{ marginBottom: 20 }}>
            <button
              onClick={() => void handleGenerateProposals()}
              disabled={generatingProposals}
              style={{
                fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
                padding: '6px 14px', borderRadius: 'var(--radius-sm)',
                background: 'transparent', color: 'var(--primary)',
                border: '1px solid var(--primary)', cursor: 'pointer',
                opacity: generatingProposals ? 0.6 : 1,
              }}
            >
              {generatingProposals ? 'Generating…' : 'Generate agent proposals'}
            </button>
            {proposalError && (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: '#ef4444', marginTop: 6 }}>{proposalError}</p>
            )}
          </div>
        )}

        {/* Add/Edit form */}
        {showForm && (
          <div style={{
            border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            background: 'var(--surface)', padding: 24, marginBottom: 24,
          }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: 'var(--text)', margin: '0 0 20px' }}>
              {editingRisk ? 'Edit Risk' : 'Add Risk'}
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              {/* Title */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Title *</label>
                <input
                  style={inputStyle}
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Database connection pool exhaustion under load"
                />
              </div>

              {/* Description */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Description</label>
                <textarea
                  style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Describe the risk and its potential consequences…"
                />
              </div>

              {/* Impact */}
              <div>
                <label style={labelStyle}>Impact</label>
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={form.impact}
                  onChange={e => setForm(f => ({ ...f, impact: e.target.value }))}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="CRITICAL">Critical</option>
                </select>
              </div>

              {/* Likelihood */}
              <div>
                <label style={labelStyle}>Likelihood</label>
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={form.likelihood}
                  onChange={e => setForm(f => ({ ...f, likelihood: e.target.value }))}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label style={labelStyle}>Status</label>
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                >
                  <option value="OPEN">Open</option>
                  <option value="MITIGATED">Mitigated</option>
                  <option value="ACCEPTED">Accepted</option>
                  <option value="CLOSED">Closed</option>
                  <option value="CARRIED_FORWARD">Carried forward</option>
                </select>
              </div>

              {/* Mitigation */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Mitigation Strategy</label>
                <textarea
                  style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
                  value={form.mitigationPlan}
                  onChange={e => setForm(f => ({ ...f, mitigationPlan: e.target.value }))}
                  placeholder="How will this risk be mitigated or managed?"
                />
              </div>
            </div>

            {formError && (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: '#ef4444', marginBottom: 12 }}>
                {formError}
              </p>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => void handleFormSubmit()}
                disabled={saving}
                style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}
              >
                {saving ? 'Saving…' : editingRisk ? 'Save Changes' : 'Create Risk'}
              </button>
              <button
                onClick={() => setShowForm(false)}
                style={btnGhost}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="">All Statuses</option>
            <option value="OPEN">Open</option>
            <option value="MITIGATED">Mitigated</option>
            <option value="ACCEPTED">Accepted</option>
            <option value="CLOSED">Closed</option>
            <option value="CARRIED_FORWARD">Carried forward</option>
          </select>
          <select value={filterImpact} onChange={e => setFilterImpact(e.target.value)} style={selectStyle}>
            <option value="">All Impact Levels</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>
          <select value={filterPhase} onChange={e => setFilterPhase(e.target.value)} style={selectStyle}>
            <option value="">All Phases</option>
            {PHASE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {(hasCarriedForwardRisks || filterCarriedForwardToPhase) && (
            <select
              value={filterCarriedForwardToPhase}
              onChange={e => setFilterCarriedForwardToPhase(e.target.value)}
              style={selectStyle}
            >
              <option value="">Carried forward to: All</option>
              {PHASE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
          {(filterStatus || filterImpact || filterPhase || filterCarriedForwardToPhase) && (
            <button
              onClick={() => {
                setFilterStatus(''); setFilterImpact(''); setFilterPhase('');
                setFilterCarriedForwardToPhase('');
              }}
              style={{ ...selectStyle, background: 'transparent', color: 'var(--text-secondary)' }}
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', fontSize: 14 }}>Loading…</p>
        ) : error ? (
          <p style={{ fontFamily: 'var(--font-body)', color: '#ef4444', fontSize: 14 }}>{error}</p>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, marginBottom: 16 }}>
              {risks.length === 0
                ? 'No risks found. Add a risk manually or sync from your sprint backlog.'
                : 'No risks match the current filters.'}
            </p>
            {risks.length === 0 && (
              <button onClick={openAddForm} style={btnPrimary}>
                + Add Your First Risk
              </button>
            )}
          </div>
        ) : (
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            {/* Table header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '80px 1fr 110px 110px 120px 130px 60px',
              gap: 0, padding: '10px 16px',
              background: 'var(--surface)', borderBottom: '1px solid var(--border)',
            }}>
              {['#', 'Title', 'Impact', 'Likelihood', 'Status', 'Phase', ''].map(h => (
                <span key={h} style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {h}
                </span>
              ))}
            </div>

            {/* Rows */}
            {filtered.map(risk => (
              <div key={risk.id}>
                <div
                  onClick={() => { setExpandedId(expandedId === risk.id ? null : risk.id); setDispositionError(null); }}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '80px 1fr 110px 110px 120px 130px 60px',
                    gap: 0, padding: '12px 16px',
                    borderBottom: '1px solid var(--border)',
                    cursor: 'pointer',
                    background: expandedId === risk.id ? 'rgba(99,102,241,0.04)' : 'transparent',
                    transition: 'background 0.1s',
                  }}
                >
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-secondary)', alignSelf: 'center' }}>
                    {risk.externalId ?? '—'}
                  </span>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', alignSelf: 'center', paddingRight: 12 }}>
                    {risk.title}
                  </span>
                  <span style={{ alignSelf: 'center' }}>
                    <Badge text={risk.impact} style={IMPACT_STYLE[risk.impact] ?? IMPACT_STYLE.LOW} />
                  </span>
                  <span style={{ alignSelf: 'center' }}>
                    <Badge text={risk.likelihood} style={IMPACT_STYLE[risk.likelihood] ?? IMPACT_STYLE.LOW} />
                  </span>
                  <span style={{ alignSelf: 'center' }} onClick={e => e.stopPropagation()}>
                    {editingStatusId === risk.id ? (
                      <select
                        autoFocus
                        value={risk.status}
                        onBlur={() => setEditingStatusId(null)}
                        onChange={e => void handleStatusChange(risk, e.target.value)}
                        style={{ ...selectStyle, fontSize: 11 }}
                      >
                        <option value="OPEN">Open</option>
                        <option value="MITIGATED">Mitigated</option>
                        <option value="ACCEPTED">Accepted</option>
                        <option value="CLOSED">Closed</option>
                        <option value="CARRIED_FORWARD">Carried forward</option>
                      </select>
                    ) : (
                      <span title="Click to change status" onClick={() => setEditingStatusId(risk.id)} style={{ cursor: 'pointer' }}>
                        <Badge text={risk.status} style={STATUS_STYLE[risk.status] ?? STATUS_STYLE.OPEN} />
                      </span>
                    )}
                  </span>
                  <span style={{ alignSelf: 'center' }}>
                    {/* AOQ-5 (#2921, §2.6) — a colored badge (not plain text)
                        so an Architecture-phase risk is distinguishable at a
                        glance in this project's single combined view, same
                        as the Impact/Likelihood/Status columns already are. */}
                    {risk.phaseId && isCanonicalPhase(risk.phaseId) ? (
                      <PhaseBadge phase={risk.phaseId as PhaseKey} />
                    ) : (
                      <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>—</span>
                    )}
                  </span>
                  <span
                    style={{ alignSelf: 'center', cursor: 'pointer', fontSize: 12, color: 'var(--primary)' }}
                    title="Edit risk"
                    onClick={e => { e.stopPropagation(); openEditForm(risk); }}
                  >
                    Edit
                  </span>
                </div>

                {/* Expanded detail */}
                {expandedId === risk.id && (
                  <div style={{
                    padding: '16px 24px 20px', background: 'var(--surface)',
                    borderBottom: '1px solid var(--border)',
                  }}>
                    <div style={{ marginBottom: 12 }}>
                      <OwnerReassignControl
                        projectId={projectId!}
                        entityType="risk"
                        entityId={risk.id}
                        ownerId={risk.ownerId}
                        ownership={ownership}
                        onReassigned={(newOwnerId) =>
                          setRisks(prev => prev.map(x => (x.id === risk.id ? { ...x, ownerId: newOwnerId } : x)))}
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                      {risk.description && (
                        <div>
                          <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Description</p>
                          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{risk.description}</p>
                        </div>
                      )}
                      {risk.mitigationPlan && (
                        <div>
                          <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Mitigation Plan</p>
                          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{risk.mitigationPlan}</p>
                        </div>
                      )}
                      {risk.outcome && (
                        <div>
                          <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Outcome</p>
                          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{risk.outcome}</p>
                        </div>
                      )}
                      {/* #2544 */}
                      {risk.status === 'CARRIED_FORWARD' && (
                        <div>
                          <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Carried Forward</p>
                          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>
                            To {risk.carriedForwardToPhase ? canonicalPhaseLabel(risk.carriedForwardToPhase) : '—'}, owner: {risk.carriedForwardOwner ?? '—'}
                          </p>
                        </div>
                      )}
                    </div>
                    {!risk.description && !risk.mitigationPlan && !risk.outcome && (
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>No additional details recorded.</p>
                    )}
                    {/* #2544 (Model A generalized) — the shared DISPOSE card,
                        parameterized with Risk's own option-set. Shown for
                        any still-OPEN risk, with or without an agent
                        proposal (the proposal panel above the options is
                        simply omitted when there is none — a manual
                        dispose). */}
                    {risk.status === 'OPEN' && (
                      <DispositionCard
                        proposal={risk.proposal ? {
                          optionLabel: risk.proposal.disposition === 'MITIGATE' ? 'Mitigate' : risk.proposal.disposition === 'ACCEPT' ? 'Accept' : 'Carry forward',
                          summary:
                            risk.proposal.disposition === 'MITIGATE' ? (risk.proposal.proposedMitigationPlan ?? '')
                            : risk.proposal.disposition === 'ACCEPT' ? (risk.proposal.proposedRationale ?? '')
                            : `To ${risk.proposal.proposedCarryForwardPhase ? canonicalPhaseLabel(risk.proposal.proposedCarryForwardPhase) : '(unspecified)'}, owner: ${risk.proposal.proposedCarryForwardOwner ?? '(unspecified)'}`,
                          basis: risk.proposal.basis,
                        } : null}
                        options={DISPOSITION_OPTIONS}
                        defaultOptionKey={risk.proposal?.disposition}
                        defaultFieldValues={risk.proposal ? {
                          mitigationPlan: risk.proposal.proposedMitigationPlan ?? '',
                          rationale: risk.proposal.proposedRationale ?? '',
                          // #2611 (R1) — a proposal generated before this fix (or by a
                          // non-compliant model response) could hold a stale non-canonical
                          // value; pre-filling it would leave the dropdown unselected while
                          // still enabling Confirm on a value the server will 422 on. Guard
                          // it here so an invalid stale value never reaches the dropdown.
                          carryForwardPhase: risk.proposal.proposedCarryForwardPhase && isCanonicalPhase(risk.proposal.proposedCarryForwardPhase)
                            ? risk.proposal.proposedCarryForwardPhase
                            : '',
                          carryForwardOwner: risk.proposal.proposedCarryForwardOwner ?? '',
                        } : undefined}
                        submitting={dispositioningId === risk.id}
                        // Only one row is ever expanded at a time (expandedId
                        // is a single value, not a set), so gating on it here
                        // is sufficient row-scoping for this single shared
                        // error state — no separate per-row error map needed.
                        errorMessage={expandedId === risk.id ? dispositionError : null}
                        onAccept={(optionKey, fieldValues) => void handleAcceptDisposition(risk, optionKey, fieldValues)}
                      />
                    )}
                    {projectId && (
                      <PKGNeighbourhoodPanel projectId={projectId} anchorNodeId={risk.pkgNodeId} />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </main>
  );
}
